import { useEffect } from 'react';
import type { PlayerProfile } from '@goodtime/engine';
import { authHeaders, BASE } from '../api/client';

/**
 * T1 gem economy (2026-05-28) — server sync for slot unlocks.
 *
 * <p>Listens for {@code slot-unlocked} events on the
 * {@link PlayerProfile} and POSTs to the matching server endpoint so
 * the player's {@code wallets.unlocked_slot_ids} stays aligned with
 * the client-side state. Fire-and-forget — local profile is the
 * authoritative source of truth for the immediate UX; server reconciles
 * asynchronously.
 *
 * <p>Failure modes:
 * <ul>
 *   <li>4xx — server rejected the purchase (e.g. 402 insufficient gems
 *       because the server side wasn't yet credited from a match
 *       payout). Logged to the console and (U12 M2, 2026-05-31)
 *       emitted as a {@code sync-error} event on the profile so the
 *       host's {@code SyncErrorBanner} can surface a toast. Local
 *       unlock is retained; server catches up on next match payout or
 *       page-refresh wallet GET.</li>
 *   <li>Network error — same: logged, emitted as {@code sync-error},
 *       retried implicitly on the next successful purchase (no retry
 *       queue in v1).</li>
 * </ul>
 *
 * <p>{@code playerId} null = solo-only context (no server wallet to
 * sync to); the hook becomes a no-op. Requests go through the shared
 * {@code BASE} (api/client) so they resolve to the API origin in prod.
 */
export function useSlotUnlockSync(
  profile: PlayerProfile,
  playerId: string | null,
): void {
  useEffect(() => {
    if (!playerId) return;
    const sub = profile.events.on('slot-unlocked', async (event) => {
      try {
        const res = await fetch(
          `${BASE}/players/${encodeURIComponent(playerId)}/wallet/spend-gems-for-slot`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
            body: JSON.stringify({ slotId: event.slotId }),
          },
        );
        if (!res.ok) {
          const reason = `HTTP ${res.status}`;
          console.warn(
            `[useSlotUnlockSync] server rejected slot unlock ` +
            `slot=${event.slotId} status=${res.status}`,
          );
          profile.events.emit({
            kind: 'sync-error',
            operation: 'slot-unlock',
            message: reason,
          });
        }
      } catch (err) {
        const reason = err instanceof Error
          ? err.message
          : String(err);
        console.warn(
          `[useSlotUnlockSync] slot unlock POST failed slot=${event.slotId}`,
          err,
        );
        profile.events.emit({
          kind: 'sync-error',
          operation: 'slot-unlock',
          message: reason,
        });
      }
    });
    return () => { sub.dispose(); };
  }, [profile, playerId]);
}
